/**
 * F-146 / T-107-001/002/003 — Cross-city Texas compliance dashboard.
 *
 * Combines the Dallas + Houston portfolio views: an overall 0-100 compliance
 * score (portfolio), per-city summary tiles, and a cross-city top-risks drill
 * (top risk properties with score + reasons), backed by the existing /api/texas
 * contracts (complianceDashboard, strPortfolio, dallasAuditCycle,
 * houstonHighRisk). Inspection countdowns surface for Dallas (audit cycle) and
 * citation tracking for Houston (HRRB). Every backend response is normalized
 * defensively so partial availability degrades instead of crashing.
 */
import React, { useCallback, useEffect, useState } from 'react';
import texasApi, { StrPortfolio, StrPortfolioProperty, CompliancePortfolioSummary, HoustonCitation } from '../../services/texasApi';

const num = (v: any): number => Number(v) || 0;
const pick = (obj: any, keys: string[]): any => {
    if (!obj) return undefined;
    for (const k of keys) {
        if (obj[k] !== undefined && obj[k] !== null) return obj[k];
    }
    return undefined;
};

const clamp = (v: number): number => Math.max(0, Math.min(100, num(v)));

export interface AuditCounts {
    overdue: number;
    upcoming: number;
}

export function normalizeAuditDashboard(raw: any): { records: any[]; counts: AuditCounts } {
    const records: any[] = Array.isArray(raw?.records)
        ? raw.records
        : Array.isArray(raw?.items)
        ? raw.items
        : Array.isArray(raw?.properties)
        ? raw.properties
        : [];
    const counts = records.reduce<AuditCounts>(
        (acc, r) => {
            if (num(r.daysOverdue) > 0 || String(r.status || '').toLowerCase() === 'overdue') acc.overdue += 1;
            const d = num(r.daysUntilInspection);
            if (d > 0 && d <= 30) acc.upcoming += 1;
            return acc;
        },
        { overdue: 0, upcoming: 0 },
    );
    return { records, counts };
}

export function countdownText(days: number | null | undefined): string {
    if (days === null || days === undefined || Number.isNaN(num(days))) return '—';
    const d = num(days);
    if (d < 0) return 'Overdue';
    return `${d}d`;
}

export const scoreClass = (score: number): string => {
    const s = clamp(score);
    if (s >= 80) return 'text-green-600';
    if (s >= 50) return 'text-amber-600';
    return 'text-red-600';
};

interface TopRiskRow {
    propertyId: string;
    propertyName?: string;
    score: number;
    reasons?: string[];
    city: string | null;
    str?: StrPortfolioProperty | null;
}

type CityFilter = 'all' | 'dallas' | 'houston';

export const MultiCityComplianceDashboard: React.FC = () => {
    const [portfolio, setPortfolio] = useState<CompliancePortfolioSummary | null>(null);
    const [dallas, setDallas] = useState<StrPortfolio | null>(null);
    const [houston, setHouston] = useState<StrPortfolio | null>(null);
    const [audit, setAudit] = useState<AuditCounts>({ overdue: 0, upcoming: 0 });
    const [hrrb, setHrrb] = useState<{ needingTraining: number; overdueRegistration: number }>({ needingTraining: 0, overdueRegistration: 0 });
    const [filter, setFilter] = useState<CityFilter>('all');
    const [selected, setSelected] = useState<TopRiskRow | null>(null);
    const [drill, setDrill] = useState<{ score: number | null; reasons?: string[]; citations: number; inspection: string | null } | null>(null);
    const [drillBusy, setDrillBusy] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [p, d, h, a, hr] = await Promise.allSettled([
                texasApi.complianceDashboard.getPortfolio(),
                texasApi.strPortfolio.getPortfolio('dallas'),
                texasApi.strPortfolio.getPortfolio('houston'),
                texasApi.dallasAuditCycle.getDashboard(),
                texasApi.houstonHighRisk.getPortfolioSummary(),
            ]);
            setPortfolio(p.status === 'fulfilled' ? p.value : null);
            setDallas(d.status === 'fulfilled' ? d.value : null);
            setHouston(h.status === 'fulfilled' ? h.value : null);
            setAudit(normalizeAuditDashboard(a.status === 'fulfilled' ? a.value : null).counts);
            if (hr.status === 'fulfilled') {
                setHrrb({
                    needingTraining: num(pick(hr.value, ['propertiesNeedingTraining', 'needingTraining', 'trainingIncomplete'])),
                    overdueRegistration: num(pick(hr.value, ['propertiesOverdueRegistration', 'overdueRegistration', 'overdue'])),
                });
            }
            const rejected = [p, d, h, a, hr].filter((r) => r.status === 'rejected');
            if (rejected.length > 0) {
                const msg = (rejected[0] as PromiseRejectedResult).reason?.message;
                setError(msg ? `${msg} (some data is unavailable)` : 'Some compliance data is unavailable');
            }
        } catch (e: any) {
            setError(e?.message || 'Unable to load multi-city compliance data');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const dallasProps = React.useMemo(() => (dallas && Array.isArray(dallas.properties) ? dallas.properties : []), [dallas]);
    const houstonProps = React.useMemo(() => (houston && Array.isArray(houston.properties) ? houston.properties : []), [houston]);

    const topRisks: TopRiskRow[] = (portfolio?.topRisks || []).map((r) => {
        const str =
            (dallasProps.find((p) => p.propertyId === r.propertyId) as StrPortfolioProperty | undefined) ||
            (houstonProps.find((p) => p.propertyId === r.propertyId) as StrPortfolioProperty | undefined) ||
            null;
        const city = str?.city ? String(str.city).toLowerCase() : str ? str.city : null;
        return { propertyId: r.propertyId, propertyName: r.propertyName, score: clamp(num(r.score)), reasons: r.reasons, city, str };
    });
    const visibleRisks = topRisks.filter((r) => filter === 'all' || r.city === filter);

    const openDrill = async (row: TopRiskRow) => {
        setSelected(row);
        setDrillBusy(true);
        setDrill(null);
        try {
            const isHouston = row.city === 'houston';
            const isDallas = row.city === 'dallas';
            const jobs = [
                texasApi.complianceDashboard.getPropertyScore(row.propertyId),
                isHouston
                    ? texasApi.houstonHighRisk.getCitations(row.propertyId)
                    : isDallas
                    ? texasApi.dallasAuditCycle.getRecord(row.propertyId)
                    : Promise.resolve(null),
            ];
            const [scoreRes, extraRes] = await Promise.allSettled(jobs);
            const score = scoreRes.status === 'fulfilled' ? clamp(num(scoreRes.value?.score)) : null;
            const reasons = scoreRes.status === 'fulfilled' ? (Array.isArray(scoreRes.value?.reasons) ? scoreRes.value.reasons : undefined) : undefined;
            let citations = 0;
            let inspection: string | null = null;
            if (extraRes.status === 'fulfilled' && extraRes.value) {
                if (isHouston) {
                    const list: HoustonCitation[] = Array.isArray(extraRes.value) ? extraRes.value : Array.isArray(extraRes.value?.citations) ? extraRes.value.citations : [];
                    citations = list.filter((c) => String(c.status || '').toLowerCase() !== 'closed').length;
                } else if (isDallas) {
                    const d = num(pick(extraRes.value, ['daysUntilInspection', 'daysUntil']));
                    inspection = pick(extraRes.value, ['nextInspectionDate', 'scheduledDate']) || null;
                    inspection = d > 0 ? `${d}d` : inspection;
                }
            }
            setDrill({ score, reasons, citations, inspection });
        } catch (e: any) {
            setDrill({ score: null, reasons: undefined, citations: 0, inspection: null });
        } finally {
            setDrillBusy(false);
        }
    };

    const dallasSummary = dallas?.summary;
    const houstonSummary = houston?.summary;

    return (
        <div className="texas-page-container" data-testid="multi-city-dashboard">
            <h1>Multi-City Texas Compliance</h1>
            <p className="page-description">
                Dallas + Houston compliance in one view — portfolio score, city summaries, and top-risk drill-downs.
            </p>

            {error && <div className="error" data-testid="mc-error">{error}</div>}

            {loading && <p className="no-results" data-testid="mc-loading">Loading cross-city compliance…</p>}

            {!loading && !portfolio && !dallas && !houston && (
                <div className="no-results" data-testid="mc-empty">
                    No compliance data available yet. Portfolios appear once properties are loaded and scored.
                </div>
            )}

            {!loading && (portfolio || dallas || houston) && (
                <>
                    <div className="result-grid">
                        <div className="result-card" data-testid="mc-overall-score">
                            <strong className={scoreClass(portfolio?.score ?? 0)}>
                                {portfolio?.score !== undefined ? clamp(num(portfolio.score)).toFixed(0) : '—'}
                            </strong>
                            <span>Portfolio compliance score (0-100)</span>
                        </div>
                        <div className="result-card" data-testid="mc-city-summary-dallas">
                            <strong>{dallasSummary ? num(dallasSummary.totalProperties) : '—'}</strong>
                            <span>Dallas properties(exp {dallasSummary ? num(dallasSummary.expiredRegistrations) : '—'}, unreg {dallasSummary ? num(dallasSummary.unregisteredProperties) : '—'})</span>
                            <span className="text-xs text-gray-500">
                                {audit.overdue > 0 ? `${audit.overdue} inspections overdue · ` : ''}
                                {audit.upcoming > 0 ? `${audit.upcoming} due in 30d` : ` ${audit.overdue > 0 ? '' : 'no inspection pressure'}`}
                            </span>
                        </div>
                        <div className="result-card" data-testid="mc-city-summary-houston">
                            <strong>{houstonSummary ? num(houstonSummary.totalProperties) : '—'}</strong>
                            <span>Houston properties</span>
                            <span className="text-xs text-gray-500">
                                {hrrb.needingTraining > 0 ? `${hrrb.needingTraining} need Blue Star training` : ''}
                                {hrrb.overdueRegistration > 0 ? `${hrrb.needingTraining > 0 ? ' · ' : ''}${hrrb.overdueRegistration} at citation trigger` : ''}
                            </span>
                        </div>
                    </div>

                    <div className="action-bar">
                        {(['all', 'dallas', 'houston'] as CityFilter[]).map((c) => (
                            <button
                                key={c}
                                className={filter === c ? 'lease-input-btn active' : 'lease-input-btn'}
                                onClick={() => setFilter(c)}
                                data-testid={`mc-city-filter-${c}`}
                            >
                                {c === 'all' ? 'All cities' : c === 'dallas' ? 'Dallas' : 'Houston'}
                            </button>
                        ))}
                    </div>

                    {visibleRisks.length > 0 && (
                        <div className="result-section">
                            <h2>Top risks</h2>
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-left text-gray-500 border-b">
                                        <th className="py-2 pr-4">Property</th>
                                        <th className="py-2 pr-4">City</th>
                                        <th className="py-2 pr-4">Score</th>
                                        <th className="py-2 pr-4">Registration</th>
                                        <th className="py-2 pr-4">To expiry</th>
                                        <th className="py-2">Key reasons</th>
                                    </tr>
                                </thead>
                                <tbody data-testid="mc-top-risks">
                                    {visibleRisks.map((row) => (
                                        <tr
                                            key={row.propertyId}
                                            className="border-b last:border-0 cursor-pointer hover:bg-gray-50"
                                            onClick={() => openDrill(row)}
                                            data-testid="mc-top-risk-row"
                                        >
                                            <td className="py-3 pr-4 font-medium text-gray-800">{row.propertyName || row.propertyId}</td>
                                            <td className="py-3 pr-4 capitalize">{row.city || '—'}</td>
                                            <td className={`py-3 pr-4 font-semibold ${scoreClass(row.score)}`} data-testid="mc-row-score">{row.score.toFixed(0)}</td>
                                            <td className="py-3 pr-4">
                                                {row.str ? (
                                                    row.str.registrationActive ? (
                                                        <span className="success">Active</span>
                                                    ) : (
                                                        <span className="red-warning">Not active</span>
                                                    )
                                                ) : '—'}
                                            </td>
                                            <td className="py-3 pr-4" data-testid="mc-row-countdown">
                                                {row.str ? countdownText(row.str.daysUntilExpiry) : '—'}
                                            </td>
                                            <td className="py-3 text-gray-600">
                                                {Array.isArray(row.reasons) && row.reasons.length > 0
                                                    ? row.reasons.slice(0, 2).join('; ')
                                                    : '—'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <p className="helper-text">Select a row to drill into its 0-100 score, citations, and inspection countdown.</p>
                        </div>
                    )}

                    {visibleRisks.length === 0 && topRisks.length > 0 && filter !== 'all' && (
                        <div className="no-results" data-testid="mc-filter-empty">
                            No top risks flagged for {filter === 'dallas' ? 'Dallas' : 'Houston'} right now.
                        </div>
                    )}

                    {selected && (
                        <div className="result-section" data-testid="mc-drilldown">
                            <div className="action-bar">
                                <h2 style={{ margin: 0 }}>{selected.propertyName || selected.propertyId}</h2>
                                <button className="lease-input-btn" onClick={() => setSelected(null)}>Close</button>
                            </div>
                            {drillBusy && <p className="no-results">Drilling…</p>}
                            {!drillBusy && drill && (
                                <div className="result-grid">
                                    <div className="result-card" data-testid="mc-drill-score">
                                        <strong className={scoreClass(drill.score ?? 0)}>{drill.score !== null ? drill.score.toFixed(0) : '—'}</strong>
                                        <span>Property compliance score</span>
                                    </div>
                                    <div className="result-card" data-testid="mc-drill-citations">
                                        <strong>{selected.city === 'houston' ? drill.citations : '—'}</strong>
                                        <span>{selected.city === 'houston' ? 'Open citations (Houston)' : 'Citation tracking applies to Houston properties'}</span>
                                    </div>
                                    <div className="result-card" data-testid="mc-drill-inspection">
                                        <strong>{selected.city === 'dallas' ? (drill.inspection || '—') : '—'}</strong>
                                        <span>{selected.city === 'dallas' ? 'Next Dallas inspection' : 'Inspection countdown applies to Dallas properties'}</span>
                                    </div>
                                </div>
                            )}
                            {!drillBusy && drill && Array.isArray(drill.reasons) && drill.reasons.length > 0 && (
                                <ul className="helper-text" style={{ marginTop: 12 }} data-testid="mc-drill-reasons">
                                    {drill.reasons.map((r, i) => (
                                        <li key={i}>· {r}</li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default MultiCityComplianceDashboard;